import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import InputField from './InputField';
import ResultCard from './ResultCard';
import { calculateMortgage } from '../utils/mortgageCalculations';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHome, FiDollarSign, FiPercent, FiCalendar } = FiIcons;

const MortgageCalculator = () => {
  const [inputs, setInputs] = useState({
    loanAmount: '',
    interestRate: '',
    termYears: ''
  });

  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});

  const validateInputs = () => {
    const newErrors = {};
    
    if (!inputs.loanAmount || parseFloat(inputs.loanAmount) <= 0) {
      newErrors.loanAmount = 'Please enter a valid loan amount';
    }
    
    if (!inputs.interestRate || parseFloat(inputs.interestRate) <= 0) {
      newErrors.interestRate = 'Please enter a valid interest rate';
    }
    
    if (!inputs.termYears || parseInt(inputs.termYears) <= 0) {
      newErrors.termYears = 'Please enter a valid term in years';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const calculateResults = () => {
    if (!validateInputs()) return;

    const loanAmount = parseFloat(inputs.loanAmount);
    const annualRate = parseFloat(inputs.interestRate);
    const termYears = parseInt(inputs.termYears);

    const calculatedResults = calculateMortgage(loanAmount, annualRate, termYears);
    setResults(calculatedResults);
  };

  useEffect(() => {
    if (inputs.loanAmount && inputs.interestRate && inputs.termYears) {
      const timer = setTimeout(() => {
        if (validateInputs()) {
          calculateResults();
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [inputs]);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Input Section */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-8"
        >
          <div className="flex items-center mb-6">
            <SafeIcon 
              icon={FiHome} 
              className="text-2xl text-blue-600 mr-3" 
            />
            <h2 className="text-2xl font-bold text-gray-800">
              Loan Details
            </h2>
          </div>

          <div className="space-y-6">
            <InputField
              label="Loan Amount"
              value={inputs.loanAmount}
              onChange={(value) => handleInputChange('loanAmount', value)}
              placeholder="500,000"
              icon={FiDollarSign}
              type="currency"
              error={errors.loanAmount}
            />

            <InputField
              label="Annual Interest Rate"
              value={inputs.interestRate}
              onChange={(value) => handleInputChange('interestRate', value)}
              placeholder="6.5"
              icon={FiPercent}
              type="percentage"
              error={errors.interestRate}
            />

            <InputField
              label="Loan Term (Years)"
              value={inputs.termYears}
              onChange={(value) => handleInputChange('termYears', value)}
              placeholder="30"
              icon={FiCalendar}
              type="number"
              error={errors.termYears}
            />
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={calculateResults}
            className="w-full mt-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Calculate Mortgage
          </motion.button>
        </motion.div>

        {/* Results Section */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="space-y-6"
        >
          {results ? (
            <>
              <ResultCard
                title="Monthly Payment"
                value={`$${results.monthlyPayment.toLocaleString()}`}
                subtitle="Principal & Interest"
                color="blue"
                delay={0.3}
              />

              <ResultCard
                title="Total Interest Paid"
                value={`$${results.totalInterest.toLocaleString()}`}
                subtitle={`Over ${inputs.termYears} years`}
                color="orange"
                delay={0.4}
              />

              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  First Payment Breakdown
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-xl">
                    <p className="text-sm text-gray-600 mb-1">Principal</p>
                    <p className="text-xl font-bold text-green-600">
                      ${results.firstPaymentPrincipal.toLocaleString()}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-xl">
                    <p className="text-sm text-gray-600 mb-1">Interest</p>
                    <p className="text-xl font-bold text-red-600">
                      ${results.firstPaymentInterest.toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="mt-4 bg-gray-50 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Interest Percentage:</span>
                    <span className="font-semibold text-gray-800">
                      {((results.firstPaymentInterest / results.monthlyPayment) * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <SafeIcon 
                icon={FiHome} 
                className="text-6xl text-gray-300 mx-auto mb-4" 
              />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                Enter Your Loan Details
              </h3>
              <p className="text-gray-500">
                Fill in the form to see your mortgage calculations
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default MortgageCalculator;